import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { formatDate } from '../../utils';
import { useAuth } from '../../contexts/AuthContext';
import referralService from '../../services/referralService';

const ReferralHistory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [availableRewards, setAvailableRewards] = useState(0);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemAmount, setRedeemAmount] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadReferralData();
    }
  }, [user]);

  const loadReferralData = async () => {
    setLoading(true);

    const [historyResult, statsResult] = await Promise.all([
      referralService.getReferralHistory(user.id),
      referralService.getUserReferralStats(user.id)
    ]);

    if (historyResult.success) {
      setReferrals(historyResult.data);
    }

    if (statsResult.success) {
      setStats(statsResult.data);
      const available = statsResult.data.referrals
        ?.filter(r => r.status === 'completed' && !r.is_redeemed)
        .reduce((sum, r) => sum + r.reward_amount, 0) || 0;
      setAvailableRewards(available);
    }

    setLoading(false);
  };

  const handleRedeem = async () => {
    const amount = parseFloat(redeemAmount);
    
    if (!amount || amount <= 0) {
      return;
    }

    if (amount > availableRewards) {
      return;
    }

    setRedeeming(true);

    const result = await referralService.redeemRewards(user.id, amount);

    if (result.success) {
      setShowRedeemModal(false);
      setRedeemAmount('');
      loadReferralData();
    }

    setRedeeming(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'expired':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'expired':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-secondary">Loading referral data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Referral History - ShaadiNest</title>
        <meta name="description" content="Track your referrals and earned rewards" />
      </Helmet>

      <Header />

      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <Button
                variant="outline"
                size="sm"
                iconName="ArrowLeft"
                onClick={() => navigate('/user-dashboard')}
                className="mb-4"
              >
                Back to Dashboard
              </Button>
              <h1 className="font-heading font-bold text-3xl text-text-primary">
                Referral History
              </h1>
              <p className="text-text-secondary mt-2">
                Track your referrals and manage rewards
              </p>
            </div>
          </div>

          {/* Stats Summary */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl shadow-soft p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="Users" size={24} className="text-blue-600" />
                  <span className="text-2xl font-bold text-text-primary">{stats.totalReferrals}</span>
                </div>
                <p className="text-text-secondary text-sm">Total Referrals</p>
              </div>

              <div className="bg-white rounded-xl shadow-soft p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="CheckCircle" size={24} className="text-green-600" />
                  <span className="text-2xl font-bold text-text-primary">{stats.completedReferrals}</span>
                </div>
                <p className="text-text-secondary text-sm">Completed</p>
              </div>

              <div className="bg-white rounded-xl shadow-soft p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="Clock" size={24} className="text-yellow-600" />
                  <span className="text-2xl font-bold text-text-primary">{stats.pendingReferrals}</span>
                </div>
                <p className="text-text-secondary text-sm">Pending</p>
              </div>

              <div className="bg-white rounded-xl shadow-soft p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name="DollarSign" size={24} className="text-secondary" />
                  <span className="text-2xl font-bold text-text-primary">${stats.totalRewards}</span>
                </div>
                <p className="text-text-secondary text-sm">Total Earned</p>
              </div>
            </div>
          )}

          {/* Available Rewards */}
          {availableRewards > 0 && (
            <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-6 text-white mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2">
                    ${availableRewards} Available to Redeem
                  </h3>
                  <p className="text-white/90">
                    Use your rewards on bookings and services
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowRedeemModal(true)}
                  className="bg-white text-primary border-white hover:bg-white/90"
                >
                  Redeem Now
                </Button>
              </div>
            </div>
          )}

          {/* Referral List */}
          <div className="bg-white rounded-xl shadow-soft p-6">
            <h2 className="font-heading font-semibold text-xl text-text-primary mb-6">
              All Referrals
            </h2>

            {referrals.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Users" size={48} className="text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary">
                  You haven't made any referrals yet
                </p>
                <Button
                  variant="primary"
                  onClick={() => navigate('/user-dashboard')}
                  className="mt-4"
                >
                  Start Referring Friends
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {referrals.map((referral) => (
                  <div
                    key={referral.id}
                    className="flex items-center justify-between p-4 bg-surface-50 rounded-lg hover:bg-surface-100 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(referral.status)}`}>
                        <Icon name={getStatusIcon(referral.status)} size={20} />
                      </div>
                      <div>
                        <p className="font-medium text-text-primary">
                          {referral.referred_user?.full_name || referral.referred_email}
                        </p>
                        <p className="text-sm text-text-secondary">
                          Referred {formatDate(referral.created_at)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(referral.status)}`}>
                          {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
                        </span>
                        {referral.status === 'completed' && (
                          <p className="text-sm text-text-secondary mt-1">
                            {referral.is_redeemed ? 'Redeemed' : `$${referral.reward_amount} available`}
                          </p>
                        )}
                      </div>
                      {referral.status === 'completed' && !referral.is_redeemed && (
                        <span className="text-lg font-bold text-green-600">
                          +${referral.reward_amount}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Redeem Modal */}
      {showRedeemModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="font-heading font-semibold text-xl text-text-primary mb-4">
              Redeem Rewards
            </h3>
            
            <div className="mb-6">
              <p className="text-text-secondary mb-4">
                Available balance: <span className="font-bold text-primary">${availableRewards}</span>
              </p>
              
              <label className="block text-sm font-medium text-text-primary mb-2">
                Amount to redeem
              </label>
              <input
                type="number"
                value={redeemAmount}
                onChange={(e) => setRedeemAmount(e.target.value)}
                max={availableRewards}
                min="1"
                step="1"
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter amount"
              />
              
              <div className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRedeemAmount(Math.min(25, availableRewards).toString())}
                >
                  $25
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRedeemAmount(Math.min(50, availableRewards).toString())}
                >
                  $50
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRedeemAmount(availableRewards.toString())}
                >
                  Max
                </Button>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRedeemModal(false);
                  setRedeemAmount('');
                }}
                className="flex-1"
                disabled={redeeming}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleRedeem}
                className="flex-1"
                disabled={redeeming || !redeemAmount || parseFloat(redeemAmount) > availableRewards}
              >
                {redeeming ? 'Processing...' : 'Redeem'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ReferralHistory;
